import vuexStore from "../store/store";

const DB_NAME = 'biyo'
// Bumped 5 -> 6 in Phase 5 to add indexes on the `orders` store (status,
// updated_at). Bumped 6 -> 7 to add the `void_reasons` store. Bumped 7 -> 8 to
// force the `void_reasons` store to be created on devices whose 6 -> 7 upgrade
// advanced the version without creating the store (e.g. the upgrade was blocked
// by another open tab). Bumped 8 -> 9 to add the `kitchenhub` store. The
// upgrade handler below creates stores/indexes idempotently and does NOT drop
// existing object stores or data, so live devices upgrade in place.
const DB_VERSION = 9
let DB;
const stores = [
    {
        name: "orders",
        key: "local_id",
        // Phase 5: index the columns the list views query so status /
        // updated_at lookups can use a cursor over the index instead of a full
        // object-store scan. Records missing the keyPath are simply not indexed.
        indexes: [
            { name: "status", keyPath: "status" },
            { name: "updated_at", keyPath: "updated_at" }
        ]
    },
    { name: "employee", key: "id" },
    { name: "categories", key: "id" },
    { name: "currency", key: "code" },
    { name: "discounts", key: "id" },
    { name: "products", key: "id" },
    { name: "sections", key: "id" },
    { name: "store", key: "id" },
    { name: "tables", key: "id" },
    { name: "tax", key: "id" },
    { name: "shift", key: "local_id" },
    { name: "customers", key: "id" },
    { name: "orderList", key: "local_id" },
    { name:"refundedOrderList",key:"local_id"},
    { name:"giftCards", key:"gift_card_number"},
    { name:"giftCardsPayments", key:"local_id"},
    { name: "promotions", key: "id" },
    { name: "notifications", key: "id" },
    { name: "orderSyncOutbox", key: "key" },
    { name: "void_reasons", key: "id" },
    { name: "kitchenhub", key: "id" }
]

export default {
    async initialize() {
        return new Promise((resolve, reject) => {
            if(DB) return resolve(DB)
            let request = window.indexedDB.open(DB_NAME,DB_VERSION)
            request.onerror = e => reject(e)
            // Fired when an open connection (e.g. another tab) blocks the version
            // upgrade. The new object stores won't exist until that tab closes.
            request.onblocked = () => {
                console.warn(`IndexedDB upgrade to v${DB_VERSION} is blocked — close other tabs of this app and reload.`)
            }
            request.onsuccess = e => { DB = e.target.result; resolve(DB) }
            request.onupgradeneeded = e => {
                let db = e.target.result
                // The versionchange transaction is needed to fetch existing
                // object stores so indexes can be added without recreating them.
                const upgradeTransaction = e.target.transaction
                stores.forEach(store => {
                    try {
                        let objectStore
                        if (!db.objectStoreNames.contains(store.name)) {
                            objectStore = db.createObjectStore(store.name, { keyPath: store.key })
                        } else if (upgradeTransaction) {
                            objectStore = upgradeTransaction.objectStore(store.name)
                        }

                        // Create declared indexes idempotently. Existing data is
                        // left intact; only missing indexes are added.
                        if (objectStore && Array.isArray(store.indexes)) {
                            store.indexes.forEach(idx => {
                                if (!objectStore.indexNames.contains(idx.name)) {
                                    objectStore.createIndex(idx.name, idx.keyPath, idx.options || {})
                                }
                            })
                        }
                    }
                    catch (err) {
                        console.error(err);
                    }
                })
            }
        })
    },

    async saveToDb(key, data) {
        let db = await this.initialize();
        return new Promise((resolve) => {
            try {
                // Store may not exist yet if a newer DB version's upgrade hasn't
                // run on this device (e.g. another tab holds the old connection).
                if (!db.objectStoreNames.contains(key)) {
                    console.warn(`saveToDb: object store '${key}' not found, skipping`);
                    resolve();
                    return;
                }
                let trans = db.transaction([key], 'readwrite');
                trans.oncomplete = () => resolve();
                let store = trans.objectStore(key);
                let keyPath = store.keyPath;

                const validateKeyPath = (obj) => {
                    if (!keyPath) return true;

                    // Handle null/empty tax - this is expected when store has no tax configured
                    if (key === 'tax' && (!obj || obj === '' || obj === null)) {
                        return false;
                    }

                    if (!obj || !obj[keyPath]) {
                        // Auto-generate local_id for order-related and shift stores if missing
                        if ((key === 'orders' || key === 'orderList' || key === 'refundedOrderList' || key === 'giftCardsPayments' || key === 'shift') && keyPath === 'local_id') {
                            const terminal = vuexStore.getters["config/terminal"];
                            const terminalId = terminal ? terminal.id : 'unknown';
                            const timestamp = Date.now();
                            obj[keyPath] = `${terminalId}_${timestamp}`;
                            console.warn(`Auto-generated ${keyPath} for ${key}:`, obj[keyPath]);
                        } else {
                            console.error(`Error: Object missing key field '${keyPath}' for store '${key}'`, obj);
                            return false;
                        }
                    }
                    return true;
                };

                if (Array.isArray(data)) {
                    data.forEach(item => {
                        if (validateKeyPath(item)) {
                            store.put(item);
                        }
                    });
                } else {
                    if (validateKeyPath(data)) {
                        store.put(data);
                    }
                }
            } catch (err) {
                console.error(err);
            }
        });
    },

    async deleteFromDb(key, keyValue) {
        if (!keyValue) {
            return;
        }
        const db = await this.initialize();
        return new Promise((resolve, reject) => {
            try {
                const trans = db.transaction([key], 'readwrite');
                trans.oncomplete = () => resolve();
                trans.onerror = (event) => reject(event);
                const store = trans.objectStore(key);
                store.delete(keyValue);
            } catch (err) {
                reject(err);
            }
        });
    },

    async getDbData(key) {
        let db = await this.initialize();
        return new Promise(resolve => {
            try {
                // Store may not exist yet if a newer DB version's upgrade hasn't
                // run on this device (e.g. another tab holds the old connection).
                if (!db.objectStoreNames.contains(key)) {
                    console.warn(`getDbData: object store '${key}' not found, returning []`);
                    resolve([]);
                    return;
                }
                let trans = db.transaction([key], 'readonly');
                trans.oncomplete = () => {
                    resolve(dbData);
                };
                let store = trans.objectStore(key);
                let dbData = [];
                store.openCursor().onsuccess = e => {
                    let cursor = e.target.result;
                    if (cursor) {
                        dbData.push(cursor.value)
                        cursor.continue();
                    }
                };
            }
            catch (err) {
                console.error(err);
            }
		});
    },

    /**
     * Fetch orders for a single status using the `status` index (Phase 5).
     * Falls back to a filtered full-cursor scan if the index is unavailable
     * (e.g. the v6 upgrade has not yet run on this device), so callers always
     * get correct results.
     *
     * @param {number|string} status
     * @returns {Promise<Array>}
     */
    async getOrdersByStatus(status) {
        const db = await this.initialize();
        return new Promise(resolve => {
            const result = [];
            try {
                const trans = db.transaction(["orders"], "readonly");
                const store = trans.objectStore("orders");
                trans.oncomplete = () => resolve(result);
                trans.onerror = () => resolve(result);

                const onCursor = (predicate) => e => {
                    const cursor = e.target.result;
                    if (cursor) {
                        if (!predicate || predicate(cursor.value)) result.push(cursor.value);
                        cursor.continue();
                    }
                };

                if (store.indexNames.contains("status")) {
                    store.index("status").openCursor(IDBKeyRange.only(status)).onsuccess = onCursor(null);
                } else {
                    store.openCursor().onsuccess = onCursor(value => value && value.status === status);
                }
            } catch (err) {
                console.error(err);
                resolve(result);
            }
        });
    },

    /**
     * Fetch orders updated at/after the given timestamp using the `updated_at`
     * index (Phase 5). Falls back to a filtered full-cursor scan when the index
     * is unavailable.
     *
     * @param {string} sinceIso - ISO timestamp lower bound (inclusive)
     * @returns {Promise<Array>}
     */
    async getOrdersUpdatedSince(sinceIso) {
        const db = await this.initialize();
        return new Promise(resolve => {
            const result = [];
            try {
                const trans = db.transaction(["orders"], "readonly");
                const store = trans.objectStore("orders");
                trans.oncomplete = () => resolve(result);
                trans.onerror = () => resolve(result);

                const onCursor = (predicate) => e => {
                    const cursor = e.target.result;
                    if (cursor) {
                        if (!predicate || predicate(cursor.value)) result.push(cursor.value);
                        cursor.continue();
                    }
                };

                if (store.indexNames.contains("updated_at")) {
                    store.index("updated_at").openCursor(IDBKeyRange.lowerBound(sinceIso)).onsuccess = onCursor(null);
                } else {
                    store.openCursor().onsuccess = onCursor(value => value && value.updated_at && value.updated_at >= sinceIso);
                }
            } catch (err) {
                console.error(err);
                resolve(result);
            }
        });
    },

    async clearAllData(store = null) {
        /**
         * @param {Array ,String} [store] - array of index db store names of name of index db store
         *
         * @example
         *  clearAllData()
         *  clearAllData(["orders","categories"])
         *  clearAllData("orders")
         */
        const db = await this.initialize()
        return new Promise((resolve, reject) => {
            try {
                const requested = !store
                    ? stores.map(item => item.name)
                    : Array.isArray(store)
                        ? store
                        : [store];

                // Skip stores that don't exist in the live DB yet (e.g. a store
                // added in a newer DB version whose upgrade hasn't run on this
                // device because another tab still holds the old connection open).
                const storeNames = requested.filter(name => db.objectStoreNames.contains(name));

                if (storeNames.length === 0) {
                    resolve();
                    return;
                }

                let completed = 0;
                const finishOne = () => {
                    completed += 1;
                    if (completed === storeNames.length) {
                        resolve();
                    }
                };

                storeNames.forEach(storeName => {
                    const trans = db.transaction(storeName, 'readwrite')
                    const requestedStore = trans.objectStore(storeName)
                    requestedStore.clear()
                    trans.oncomplete = finishOne
                    trans.onerror = (event) => reject(event)
                })
            }
            catch (err) {
                reject(err)
            }

        })
    }

}
